/**
 * Two-factor (TOTP) management — lives in Settings.
 *
 * States:
 *   - off          → "Enable" button kicks off the QR + verify flow.
 *   - pending      → setup ran but never verified; show "Finish setup".
 *   - on           → green status, "X of 8 backup codes left", Disable +
 *                    Regenerate backup codes actions.
 *
 * The enable flow is multi-step and stateful, so it lives in a small
 * inline panel rather than a modal — easier to scan, and the QR is meant
 * to be seen while the user opens their authenticator app.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck,
  ShieldOff,
  KeyRound,
  Copy,
  Check,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { totpApi, type TotpSetup } from '@/api/auth';

type View =
  | { kind: 'idle' }
  | { kind: 'setup'; data: TotpSetup }
  | { kind: 'codes'; codes: string[]; reason: 'enabled' | 'regenerated' }
  | { kind: 'disable' };

export function SecurityPanel() {
  const qc = useQueryClient();
  const [view, setView] = useState<View>({ kind: 'idle' });
  const status = useQuery({
    queryKey: ['totp', 'status'],
    queryFn: () => totpApi.status(),
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['totp', 'status'] });

  const setup = useMutation({
    mutationFn: () => totpApi.setup(),
    onSuccess: (data) => {
      setView({ kind: 'setup', data });
      void invalidate();
    },
    onError: () => toast.error('Could not start setup. Try again.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="size-4" />
          Two-factor authentication
        </CardTitle>
        <CardDescription>
          Require a 6-digit code from your authenticator app at every sign-in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status.isPending && <Skeleton className="h-10 w-full" />}

        {status.data && view.kind === 'idle' && (
          <IdleState
            enrolled={status.data.enrolled}
            pending={status.data.pending}
            unusedBackupCodes={status.data.unused_backup_codes}
            onEnable={() => setup.mutate()}
            onDisable={() => setView({ kind: 'disable' })}
            onRegenerate={(codes) =>
              setView({ kind: 'codes', codes, reason: 'regenerated' })
            }
            enabling={setup.isPending}
            invalidate={invalidate}
          />
        )}

        {view.kind === 'setup' && (
          <SetupStep
            data={view.data}
            onCancel={() => setView({ kind: 'idle' })}
            onEnabled={(codes) => {
              void invalidate();
              setView({ kind: 'codes', codes, reason: 'enabled' });
            }}
          />
        )}

        {view.kind === 'codes' && (
          <CodesDisplay
            codes={view.codes}
            reason={view.reason}
            onDone={() => setView({ kind: 'idle' })}
          />
        )}

        {view.kind === 'disable' && (
          <DisableStep
            onCancel={() => setView({ kind: 'idle' })}
            onDone={() => {
              void invalidate();
              setView({ kind: 'idle' });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Idle (current status + primary action) ────────────────────────────────

function IdleState({
  enrolled,
  pending,
  unusedBackupCodes,
  onEnable,
  onDisable,
  onRegenerate,
  enabling,
  invalidate,
}: {
  enrolled: boolean;
  pending: boolean;
  unusedBackupCodes: number;
  onEnable: () => void;
  onDisable: () => void;
  onRegenerate: (codes: string[]) => void;
  enabling: boolean;
  invalidate: () => Promise<void>;
}) {
  const [regenCode, setRegenCode] = useState('');
  const [regenOpen, setRegenOpen] = useState(false);
  const regen = useMutation({
    mutationFn: () => totpApi.regenerateBackupCodes(regenCode.trim()),
    onSuccess: ({ codes }) => {
      setRegenCode('');
      setRegenOpen(false);
      void invalidate();
      onRegenerate(codes);
    },
    onError: () => toast.error('That code is incorrect. Try again.'),
  });

  if (enrolled) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <ShieldCheck className="size-4 text-emerald-600" />
          <p className="font-medium text-emerald-900">2FA is on for this account.</p>
        </div>
        <p className="text-xs text-[color:var(--kz-color-ink-secondary)]">
          {unusedBackupCodes} of 8 backup codes remaining.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onDisable}>
            <ShieldOff className="size-4" />
            Disable 2FA
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRegenOpen((v) => !v)}>
            <KeyRound className="size-4" />
            Regenerate backup codes
          </Button>
        </div>

        {regenOpen && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (regenCode.trim()) regen.mutate();
            }}
            className="grid gap-2 rounded-md border border-[color:var(--kz-color-border-subtle)] p-3"
          >
            <Label htmlFor="regen-code" className="text-xs">
              Enter a fresh 6-digit code from your authenticator
            </Label>
            <div className="flex gap-2">
              <Input
                id="regen-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123 456"
                maxLength={8}
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value)}
                className="tracking-[0.4em] text-center"
              />
              <Button type="submit" size="sm" disabled={regen.isPending || !regenCode.trim()}>
                {regen.isPending ? <Loader2 className="animate-spin" /> : 'Regenerate'}
              </Button>
            </div>
            <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">
              Existing backup codes will stop working immediately.
            </p>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/70 p-3 text-sm">
        <ShieldOff className="size-4 text-amber-700" />
        <p className="text-amber-900">
          {pending ? '2FA setup is unfinished. Continue to enable it.' : 'Your account is protected by password only.'}
        </p>
      </div>
      <Button onClick={onEnable} disabled={enabling}>
        {enabling ? (
          <>
            <Loader2 className="animate-spin" />
            Starting…
          </>
        ) : (
          <>
            <ShieldCheck className="size-4" />
            {pending ? 'Finish setup' : 'Enable two-factor'}
          </>
        )}
      </Button>
    </div>
  );
}

// ── Setup step (QR + verify) ──────────────────────────────────────────────

function SetupStep({
  data,
  onCancel,
  onEnabled,
}: {
  data: TotpSetup;
  onCancel: () => void;
  onEnabled: (codes: string[]) => void;
}) {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const enable = useMutation({
    mutationFn: () => totpApi.enable(code.trim()),
    onSuccess: ({ codes }) => onEnabled(codes),
    onError: () => toast.error('Incorrect code. Try a fresh one.'),
  });

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(data.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy.');
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-[12rem_1fr] md:items-start">
      <div className="kz-qr-plate grid place-items-center rounded-md border border-[color:var(--kz-color-border-subtle)] bg-white p-2">
        <img src={data.qr_data_url} alt="2FA QR code" className="size-44" />
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium">1. Scan with your authenticator app</p>
          <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
            Use Google Authenticator, 1Password, Bitwarden, Authy, or any TOTP app.
          </p>
        </div>
        <div>
          <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
            Or enter this key manually:
          </p>
          <div className="mt-1 flex items-center gap-2 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)] p-2 font-mono text-xs">
            <span className="flex-1 break-all">{data.secret}</span>
            <button
              type="button"
              onClick={copySecret}
              className="grid size-7 place-items-center rounded-md text-[color:var(--kz-color-ink-secondary)] hover:bg-white hover:text-[color:var(--kz-color-ink-primary)]"
              aria-label="Copy key"
            >
              {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
            </button>
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim()) enable.mutate();
          }}
          className="grid gap-1.5"
        >
          <Label htmlFor="enable-code" className="text-sm font-medium">
            2. Enter the 6-digit code from the app
          </Label>
          <div className="flex gap-2">
            <Input
              id="enable-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123 456"
              maxLength={8}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="tracking-[0.4em] text-center text-lg"
            />
            <Button type="submit" disabled={enable.isPending || !code.trim()}>
              {enable.isPending ? <Loader2 className="animate-spin" /> : 'Verify & enable'}
            </Button>
          </div>
        </form>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Backup codes (post-enable / post-regenerate) ──────────────────────────

function CodesDisplay({
  codes,
  reason,
  onDone,
}: {
  codes: string[];
  reason: 'enabled' | 'regenerated';
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy.');
    }
  };

  const download = () => {
    const blob = new Blob([`Kidzio.io backup codes\nGenerated ${new Date().toISOString()}\n\n${text}\n`], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kidzio-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <p className="font-semibold text-emerald-900">
          {reason === 'enabled' ? '2FA is now enabled.' : 'Fresh backup codes generated.'}
        </p>
        <p className="mt-0.5 text-xs text-emerald-800/80">
          Save these {codes.length} one-time codes somewhere safe. Each works once, in
          place of an authenticator code if you lose your device.
        </p>
      </div>
      <ul className="grid grid-cols-2 gap-1.5 rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)] p-3 font-mono text-sm">
        {codes.map((c) => (
          <li key={c} className="rounded bg-white px-2 py-1 text-center tracking-wider">
            {c}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={copyAll}>
          {copied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button variant="outline" size="sm" onClick={download}>
          Download .txt
        </Button>
        <Button size="sm" onClick={onDone}>
          I&rsquo;ve saved them
        </Button>
      </div>
    </div>
  );
}

// ── Disable step (verify before disabling) ────────────────────────────────

function DisableStep({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const disable = useMutation({
    mutationFn: () =>
      totpApi.disable(useBackup ? { backup_code: code.trim() } : { code: code.trim() }),
    onSuccess: () => {
      toast.success('Two-factor authentication disabled.');
      onDone();
    },
    onError: () => toast.error('That code is incorrect.'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim()) disable.mutate();
      }}
      className="grid gap-3"
    >
      <div className="rounded-md border border-rose-200 bg-rose-50/70 p-3 text-sm text-rose-900">
        Turning 2FA off makes your account easier to compromise. Enter a current code to confirm.
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="disable-code">
          {useBackup ? 'Backup code' : '6-digit code'}
        </Label>
        <Input
          id="disable-code"
          inputMode={useBackup ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          placeholder={useBackup ? 'ABCDE-FGHIJ' : '123 456'}
          maxLength={useBackup ? 16 : 8}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={useBackup ? 'font-mono text-center' : 'tracking-[0.4em] text-center text-lg'}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="destructive" disabled={disable.isPending || !code.trim()}>
          {disable.isPending ? <Loader2 className="animate-spin" /> : 'Disable 2FA'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <button
          type="button"
          onClick={() => {
            setUseBackup((v) => !v);
            setCode('');
          }}
          className="ml-auto text-xs font-medium text-[color:var(--kz-color-brand-primary)] hover:underline"
        >
          {useBackup ? 'Use authenticator code' : 'Use a backup code'}
        </button>
      </div>
    </form>
  );
}
